import type { EffectiveChecklistItemStatus } from '../types';
import { useChecklistRoot } from './root-context';

/** A reference to another step by id — the by-id complement to the self-scoped {@link useChecklistItemCtx}. */
type ChecklistStepRef = {
  exists: boolean;
  /** Its live status, or `undefined` if absent or still loading. */
  status: EffectiveChecklistItemStatus | undefined;
  /** Expand it (single-open, so this closes the current step). In a gated list a `locked` target may
   * not visibly expand; intended for ungated lists or steps that aren't locked. */
  open: () => void;
};

/**
 * References another step by id, so a step can coordinate with a sibling without importing it: gate
 * copy on whether the sibling `exists`, react to its `status`, or `open` it. Presence-checking keeps
 * a step compatible-but-not-reliant — render the cross-step affordance only when the sibling is there.
 */
export function useChecklistStep(id: string): ChecklistStepRef {
  const { hasItem, getStatus, setExpanded } = useChecklistRoot();
  return {
    exists: hasItem(id),
    status: getStatus(id),
    open: () => setExpanded(id, true),
  };
}
